import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Hook that records daily activity for streak tracking.
 * Calls the DB function `record_daily_activity` at most once per session/day.
 * Use `recordActivity()` after any meaningful user action.
 */
export function useRecordActivity() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const recordedRef = useRef(false);

  const recordActivity = useCallback(async () => {
    if (!user?.id || recordedRef.current) return;
    recordedRef.current = true;

    try {
      const { error } = await supabase.rpc('record_daily_activity', {
        _user_id: user.id,
      });
      if (error) {
        console.error('Error recording activity:', error);
        recordedRef.current = false; // allow retry on error
        return;
      }
      // Invalidate streak queries so widgets refresh
      queryClient.invalidateQueries({ queryKey: ['streak'] });
      queryClient.invalidateQueries({ queryKey: ['streak-ranking'] });
    } catch (err) {
      console.error('Error recording activity:', err);
      recordedRef.current = false;
    }
  }, [user?.id, queryClient]);

  return { recordActivity };
}
